import { formatInTimeZone } from 'date-fns-tz/formatInTimeZone'
import { fromZonedTime } from 'date-fns-tz/fromZonedTime'
import { toDate } from 'date-fns-tz/toDate'
import { toZonedTime } from 'date-fns-tz/toZonedTime'
import { addMilliseconds } from 'date-fns/addMilliseconds'
import { addWeeks } from 'date-fns/addWeeks'
import { endOfISOWeek } from 'date-fns/endOfISOWeek'
import { isValid } from 'date-fns/isValid'
import { enGB } from 'date-fns/locale/en-GB'
import { startOfISOWeek } from 'date-fns/startOfISOWeek'

function localWallTime(date: Date, timezone: string): Date {
  // Construct UTC fields directly: a browser-local Date can normalize another zone's
  // wall clock through the browser's own DST gap before we compare it.
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(date)
  const field = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value)
  const result = new Date(0)
  result.setUTCFullYear(field('year'), field('month') - 1, field('day'))
  result.setUTCHours(field('hour'), field('minute'), field('second'), date.getUTCMilliseconds())
  return result
}

export function parseServiceDate(value: string | Date, timezone: string): Date {
  const date = toDate(value, { timeZone: timezone })
  if (
    typeof value !== 'string' ||
    !isValid(date) ||
    !/^\d{4}-\d{2}-\d{2}(?:[T ]\d{2}:\d{2}(?::\d{2}(?:\.\d+)?)?)?$/.test(value)
  )
    return date

  const intended = toDate(value, { timeZone: 'UTC' })
  const actual = localWallTime(date, timezone)
  // Match legacy parsing: advance skipped local times by the size of the DST gap.
  const gap = intended.getTime() - actual.getTime()
  if (gap > 0) return addMilliseconds(date, gap)

  // For repeated local times, keep the earlier occurrence, including half-hour DST.
  const previousDay = addMilliseconds(date, -24 * 60 * 60 * 1000)
  const offset = actual.getTime() - date.getTime()
  const previousOffset = localWallTime(previousDay, timezone).getTime() - previousDay.getTime()
  const earlier = addMilliseconds(date, offset - previousOffset)
  return earlier < date && localWallTime(earlier, timezone).getTime() === intended.getTime()
    ? earlier
    : date
}

export function getServiceWeekStart(value: string | Date, timezone: string): Date {
  const localDate = toZonedTime(parseServiceDate(value, timezone), timezone)
  return fromZonedTime(startOfISOWeek(localDate), timezone)
}

export function getServiceWeekEnd(value: string | Date, timezone: string): Date {
  const localDate = toZonedTime(parseServiceDate(value, timezone), timezone)
  return fromZonedTime(endOfISOWeek(localDate), timezone)
}

export function shiftServiceWeek(value: Date, weeks: number, timezone: string): Date {
  return fromZonedTime(addWeeks(toZonedTime(value, timezone), weeks), timezone)
}

export function getServiceDateKey(value: string | Date, timezone: string): string | null {
  const date = parseServiceDate(value, timezone)
  return isValid(date) ? formatInTimeZone(date, timezone, 'yyyy-MM-dd') : null
}

export function formatServiceWeekDate(value: Date, timezone: string): string {
  return isValid(value)
    ? formatInTimeZone(value, timezone, 'do MMM', { locale: enGB })
    : 'Invalid date'
}
